import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router';
import { isRecordReview } from '@/utils/recordReview';
import { isSufeExamClient } from '@/utils/examClient';
import store, { useAppSelector } from '@/stores';
import { getExamInfoAsync } from '@/stores/slice/exam';
import { getStudentInfoAsync } from '@/stores/slice/user';
import i18n from '@/locales/init';
import style from './index.module.less';
import OneTest from './OneTest';
import Introduction from './Introduction/index';



type keyType2 = 'listening' | 'reading' | 'writing'

function MockStart() {

  const params = useParams<{recordId: string;}>();
  const {recordId} = params;
  const navigate = useNavigate();
  const readOnly = isRecordReview(useLocation().search);


  const data = useAppSelector((state) => state.exam.info);
  const recordData = data?.record_id === Number(recordId) ? data : undefined;
  const loadStatus = useAppSelector((state) => state.exam.loadStatus);
  const statusUpdating = !recordData || loadStatus !== 'succeeded';
  // Background checks must not unmount a verified exam's video and reset playback.
  const [verifiedRecordId, setVerifiedRecordId] = useState<string>();
  const instructionsReady = !!recordData && verifiedRecordId === recordId;
  const type = recordData?.type;
  const targetInfo = useAppSelector((state) => state.user.targetScore);
  const userId = useAppSelector((state) => state.user.userId);

  useEffect(() => {
    store.dispatch(getStudentInfoAsync());
  }, [recordId]);

  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    let request: {abort: () => void;} | undefined;
    const refresh = async () => {
      if (stopped || request) return;
      clearTimeout(timer);
      const currentRequest = store.dispatch(getExamInfoAsync({record_id: Number(recordId)}));
      request = currentRequest;
      const result = await currentRequest;
      if (stopped) return;
      request = undefined;
      let delay = 5_000;
      if (getExamInfoAsync.fulfilled.match(result)) {
        setVerifiedRecordId(recordId);
        delay = 20_000;
        const info = result.payload;
        const allScored = info.type === 2 ? info.listening.status === 4
          : info.type === 3 ? info.reading.status === 4
            : info.type === 4 ? info.writing.status === 4
              : info.type === 1 && info.listening.status === 4 && info.reading.status === 4 && info.writing.status === 4;
        if (allScored) return;
      }
      timer = setTimeout(refresh, delay);
    };
    refresh();
    window.addEventListener('online', refresh);
    return () => {
      stopped = true;
      clearTimeout(timer);
      window.removeEventListener('online', refresh);
      request?.abort?.();
    };
  }, [recordId]);

  const enableObj = useMemo(() => {
    const obj = {
      listening: false,
      reading: false,
      writing: false
    };
    if (type === 2) {
      obj.listening = true;
    } else if (type === 3) {
      obj.reading = true;
    } else if (type === 4) {
      obj.writing = true;
    } else {
      ['listening','reading','writing'].find((key) => {
        if (recordData && (recordData[key as keyType2].status === 1 || recordData[key as keyType2].status === 2)) {
          obj[key as keyType2] = true;
          return true;
        }
        return false;
      });
    }
    return obj;
  }, [recordData, type]);

  const examFinished = useMemo(() => {
    const listeningFinshed = recordData?.listening.status === 4;
    const writingFinished = recordData?.writing.status === 4
      || (recordData?.writing.status === 3 && recordData.writing.submission_pending === false);
    const readingFinished = recordData?.reading.status === 4;

    switch (recordData?.type) {
      case 1: return listeningFinshed && writingFinished && readingFinished;
      case 2: return !!listeningFinshed;
      case 3: return !!readingFinished;
      case 4: return !!writingFinished;
      default: return false;
    }

  }, [recordData]);

  const writingSubmissionPending = recordData?.type === 1
    && recordData.listening.status === 4
    && recordData.reading.status === 4
    && recordData.writing.status === 3
    && recordData.writing.submission_pending === true;

  const writingSubmissionUnresolved = recordData?.type === 1
    && recordData.listening.status === 4
    && recordData.reading.status === 4
    && recordData.writing.status === 3
    && recordData.writing.submission_pending !== false;

  useEffect(() => {
    if (!readOnly && isSufeExamClient() && writingSubmissionPending && recordId) {
      navigate(`/mockwriting/${recordId}`, {replace: true});
    }
  }, [navigate, recordId, writingSubmissionPending, readOnly]);

  const arr = [['listening', 'Listening'], ['reading', 'Reading'], ['writing','Writing']] as const;
  const showIntroduction = !readOnly && instructionsReady && arr.some(([course]) => enableObj[course] && recordData && recordData[course].status < 3);
  const videoMap = useMemo(() => ({
    listening: 'https://duy-ielts-public.oss-cn-hangzhou.aliyuncs.com/front/mock_test/listening.mp4',
    reading: 'https://duy-ielts-public.oss-cn-hangzhou.aliyuncs.com/front/mock_test/reading.mp4',
    writing: 'https://duy-ielts-public.oss-cn-hangzhou.aliyuncs.com/front/mock_test/writing.mp4'
  }), []);


  const timeMap = useMemo(() => ({
    Listening: '30',
    Reading: '60',
    Writing: '60'
  }), []);

  return (
    <div className={style.dashboard}>
      <div className={style.topBar}>
        <div className={style.info}>
          <div className={style.studentId}>{i18n.t('common.dauyan_zkn_xxxxxx', { user_id: userId })}</div>
        </div>
        {isSufeExamClient() && recordData && !writingSubmissionUnresolved && (!statusUpdating || examFinished) && (
          <button type="button" className={style.exit} onClick={() => window.close()}>{i18n.t('common.dauyan_exit_to_home')}</button>
        )}
      </div>
      <div className={style.container}>
        <div className={style.row}>
          <h1 className={style.h1}>IELTS Familiarisation Test</h1>
        </div>
        <div className={style.date}>
          <p className={style.dateTxt}>Today</p>
          <hr className={style.horizontalLine}/>
        </div>
        {statusUpdating && !instructionsReady && <p role="status">{i18n.t(loadStatus === 'failed' ? 'common.dauyan_exam_guard_retrying' : 'common.dauyan_exam_guard_checking')}</p>}
        {
          arr.map((strArr) => {
            const [keyLowerCase, key] = strArr;
            const videoUrl = videoMap[keyLowerCase];
            return (recordData?.[keyLowerCase] ? <OneTest {...recordData[keyLowerCase]} title={key} key={`${recordId}-${key}`} enable={enableObj[keyLowerCase]} videoUrl={videoUrl} time={timeMap[key]} recordId={recordId as string} target={targetInfo[keyLowerCase]} readOnly={readOnly} showScore={readOnly || examFinished} statusUpdating={statusUpdating} instructionsReady={instructionsReady}></OneTest> : null);
          })
        }
      </div>
      <div className={style.footer}>
        <span className={style.footerItem}>{i18n.t('common.dauyan_website_recorde_code')}</span>
        <span className={style.footerItem}>{i18n.t('common.dauyan_shenzhendauyan', { year: new Date().getFullYear() })}</span>
        <span className={style.footerItem}>{i18n.t('common.dauyan_phone')}</span>
      </div>
      {showIntroduction && <Introduction key={recordId}></Introduction>}
    </div>
  );
}

export default MockStart;
